import { useMemo } from 'react';
import {
  format, addMonths, subMonths, addWeeks, subWeeks, addDays, subDays,
  startOfWeek, endOfWeek,
} from 'date-fns';
import { es } from 'date-fns/locale';
import { ChevronLeft, ChevronRight, Plus, Loader2, ListTodo } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { CalendarProps, CalendarView } from './types';
import { WeekDayView } from './WeekDayView';
import { ListView } from './ListView';

export type { CalendarEvent, CalendarView, CalendarProps, CalendarTareasLayer } from './types';
export { MiniCalendar } from './MiniCalendar';

const VIEWS: { id: CalendarView; label: string }[] = [
  { id: 'week',   label: 'Semana' },
  { id: 'agenda', label: 'Lista'  },
];

function getTitle(date: Date, view: CalendarView): string {
  switch (view) {
    case 'month':
      return format(date, 'MMMM yyyy', { locale: es });
    case 'week': {
      const s = startOfWeek(date, { weekStartsOn: 1 });
      const e = endOfWeek(date, { weekStartsOn: 1 });
      return s.getMonth() === e.getMonth()
        ? `${format(s, 'd')} – ${format(e, "d 'de' MMMM yyyy", { locale: es })}`
        : `${format(s, 'd MMM', { locale: es })} – ${format(e, 'd MMM yyyy', { locale: es })}`;
    }
    case 'day':
      return format(date, "EEEE d 'de' MMMM yyyy", { locale: es });
    case 'agenda':
      return 'Próximas citas';
  }
}

function navigate(date: Date, view: CalendarView, dir: 1 | -1): Date {
  switch (view) {
    case 'month':  return dir > 0 ? addMonths(date, 1) : subMonths(date, 1);
    case 'week':   return dir > 0 ? addWeeks(date, 1)  : subWeeks(date, 1);
    case 'day':    return dir > 0 ? addDays(date, 1)   : subDays(date, 1);
    case 'agenda': return dir > 0 ? addMonths(date, 1) : subMonths(date, 1);
  }
}

export function Calendar({
  events, isLoading, readOnly, view, onViewChange,
  date, onDateChange, onEventCreate, onEventClick, onEventDrop, className, clinicaHorarios, capaTareas,
}: CalendarProps) {
  const title = useMemo(() => getTitle(date, view), [date, view]);

  function handleNewEvent() {
    const start = new Date();
    start.setMinutes(0, 0, 0);
    start.setHours(start.getHours() + 1);
    const end = new Date(start);
    end.setHours(end.getHours() + 1);
    onEventCreate?.({ start: start.toISOString(), end: end.toISOString() });
  }

  return (
    <div className={cn('flex flex-col h-full bg-white', className)}>
      {/* Toolbar */}
      <header className="flex items-center gap-2 px-4 py-2.5 border-b border-gray-100 flex-shrink-0">
        <div className="flex items-center gap-0.5">
          <button
            onClick={() => onDateChange(new Date())}
            className="px-3 py-1.5 text-xs font-medium rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors text-text-primary mr-1"
          >
            Hoy
          </button>
          <button
            onClick={() => onDateChange(navigate(date, view, -1))}
            aria-label="Anterior"
            className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors text-text-secondary hover:text-text-primary"
          >
            <ChevronLeft size={16} />
          </button>
          <button
            onClick={() => onDateChange(navigate(date, view, 1))}
            aria-label="Siguiente"
            className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors text-text-secondary hover:text-text-primary"
          >
            <ChevronRight size={16} />
          </button>
        </div>

        <h2 className="flex-1 text-sm font-semibold text-text-primary capitalize pl-1 truncate">
          {title}
        </h2>

        {isLoading && (
          <Loader2 size={14} className="text-text-secondary animate-spin flex-shrink-0" />
        )}

        {!readOnly && onEventCreate && (
          <button
            onClick={handleNewEvent}
            className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 bg-primary-500 text-white text-xs font-medium rounded-lg hover:bg-primary-600 transition-colors flex-shrink-0"
          >
            <Plus size={13} />
            Nueva cita
          </button>
        )}

        {capaTareas && (
          <button
            onClick={capaTareas.onToggle}
            aria-pressed={capaTareas.activa}
            title="Mostrar las tareas que vencen cada día"
            className={cn(
              'flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium rounded-lg border transition-colors flex-shrink-0',
              capaTareas.activa
                ? 'bg-primary-50 border-primary-200 text-primary-700'
                : 'border-gray-200 text-text-secondary hover:bg-gray-50 hover:text-text-primary',
            )}
          >
            {capaTareas.activa && capaTareas.isLoading
              ? <Loader2 size={13} className="animate-spin" />
              : <ListTodo size={13} />}
            <span className="hidden sm:inline">Tareas</span>
          </button>
        )}

        {/* View switcher */}
        <div className="flex items-center border border-gray-200 rounded-lg overflow-hidden divide-x divide-gray-200 flex-shrink-0">
          {VIEWS.map(v => (
            <button
              key={v.id}
              onClick={() => onViewChange(v.id)}
              className={cn(
                'px-2.5 py-1.5 text-xs font-medium transition-colors',
                view === v.id
                  ? 'bg-primary-500 text-white'
                  : 'text-text-secondary hover:bg-gray-50 hover:text-text-primary',
              )}
            >
              {v.label}
            </button>
          ))}
        </div>
      </header>

      {/* View content */}
      <div className="flex-1 min-h-0">
        {view === 'week' && (
          <WeekDayView
            date={date}
            events={events}
            mode="week"
            onDateChange={onDateChange}
            onEventClick={onEventClick}
            onEventCreate={onEventCreate}
            onEventDrop={onEventDrop}
            readOnly={readOnly}
            clinicaHorarios={clinicaHorarios}
            bandaTodoElDia={capaTareas?.activa ? capaTareas.renderDia : undefined}
          />
        )}
        {view === 'agenda' && (
          <ListView
            date={date}
            events={events}
            onEventClick={onEventClick}
          />
        )}
      </div>
    </div>
  );
}
